import React from "react";
import moment from "moment";
import { Link } from "react-router";
import { RaisedButton } from "material-ui";

import WorkingIndicator from "./partials/working-indicator.jsx";

import StatsBlockContainer from "./partials/stats-block-container.jsx";
import StatsBlock from "./partials/stats-block.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import UserStore from "../stores/UserStore";
import BalanceActions from "../actions/BalanceActions";
import BalanceStore from "../stores/BalanceStore";
import BalanceUtil from "../utils/BalanceUtil";
import { formatPrice, capitalize, unSnakeCase } from "../util.js";

import GenericAnalyticsTable from "./partials/generic-analytics-table.jsx";
import { stringify } from "qs";

class Balance extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasLoaded: false,
      hasMoreTransactions: false,
      balanceTransactions: null
    };
  }

  static getStores() {
    return [BalanceStore];
  }

  static getPropsFromStores() {
    return BalanceStore.getState();
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  componentDidMount() {
    BalanceActions.fetchBalance();
  }

  _loadBalanceTransactions({ accountId, startingAfter, endingBefore }) {
    this.setState({ balanceTransactions: [], loading: true });
    BalanceUtil.fetchBalanceTransactions(UserStore.getState().apiKey, {
      accountId,
      startingAfter,
      endingBefore
    })
      .then(({ has_more, transactions }) => {
        this.context.router.replace(
          `${this.props.location.pathname}?${stringify(
            {
              account_id: accountId,
              starting_after: startingAfter,
              ending_before: endingBefore
            },
            { state: { ignoreScrollBehavior: true } }
          )}`
        );

        this.setState({
          loading: false,
          hasMoreTransactions: has_more,
          balanceTransactions: transactions
        });
      })
      .catch(e => window.alert(e.message));
  }

  componentWillReceiveProps(newProps) {
    if (newProps.balance && !this.props.location.query.account_id) {
      this.context.router.replace(
        `${this.props.location.pathname}?${stringify({
          account_id: newProps.balance.balances[0].account_id
        })}`
      );
    }

    if (
      this.props.location.query.account_id &&
      !this.state.balanceTransactions
    ) {
      this._loadBalanceTransactions({
        accountId: this.props.location.query.account_id,
        startingAfter: this.props.location.query.starting_after,
        endingBefore: this.props.location.query.ending_before
      });
    }
  }

  getStyles() {
    return {};
  }

  render() {
    const styles = this.getStyles();
    const balanceData = this.props.balance;

    if (!balanceData) {
      return <WorkingIndicator fullPage={true} />;
    }

    const selectedBalance = balanceData.balances[0].balance;
    const currency = selectedBalance.available[0].currency;
    const available = selectedBalance.available[0].amount;
    const pending = selectedBalance.pending[0].amount;
    const total = available + pending;

    return (
      <div>
        <h1>Eventive Balance Ledger</h1>
        <StatsBlockContainer style={{ marginBottom: "1em" }}>
          <StatsBlock
            value={formatPrice(total, { currency })}
            label={"Current account balance"}
          />
          <StatsBlock
            value={formatPrice(available, { currency })}
            label={"Balance available to withdraw"}
          />
          <StatsBlock
            value={formatPrice(0, { currency })}
            label={"Balance available for instant payout"}
          />
        </StatsBlockContainer>
        <div style={{ marginBottom: "1em" }}>
          <div
            style={{
              float: "right",
              display: "inline-block",
              marginBottom: "1em"
            }}
          >
            <RaisedButton
              label="Previous"
              onClick={() => {
                this._loadBalanceTransactions({
                  accountId: this.props.location.query.account_id,
                  endingBefore: this.state.balanceTransactions[0].id
                });
              }}
            />{" "}
            <RaisedButton
              style={{ marginLeft: "1em" }}
              label="Next"
              onClick={() => {
                this._loadBalanceTransactions({
                  accountId: this.props.location.query.account_id,
                  startingAfter: this.state.balanceTransactions[
                    this.state.balanceTransactions.length - 1
                  ].id
                });
              }}
            />
          </div>
          <div>
            This ledger serves as the master record of what is going in and out
            of your Eventive/Stripe balance and, ultimately, your bank account.
            You can download aggregated reports using the various Bank transfer
            logs available in <Link to="/reports">Global reports</Link>.
          </div>
        </div>
        <GenericAnalyticsTable
          csvLabel="balance-log"
          noSort
          noDownload
          loading={this.state.loading}
          data={(this.state.balanceTransactions || []).map(transaction => {
            const eventiveOrderId =
              transaction.source.metadata.eventive_order ||
              (transaction.source.charge &&
                transaction.source.charge.metadata.eventive_order);

            return {
              date: {
                value: transaction.created,
                label: (
                  <span style={{ whiteSpace: "nowrap" }}>
                    {moment(transaction.created * 1000).format("lll")}
                  </span>
                )
              },
              type: {
                value: transaction.type,
                label: (
                  <span style={{ whiteSpace: "nowrap" }}>
                    {capitalize(unSnakeCase(transaction.type))}
                  </span>
                )
              },
              net: {
                value: transaction.net,
                label: (
                  <span style={{ fontWeight: "bold" }}>
                    {formatPrice(transaction.net, {
                      currency: transaction.currency,
                      accounting: true
                    })}
                  </span>
                )
              },
              amount: formatPrice(transaction.amount, {
                currency: transaction.currency,
                accounting: true
              }),
              fee: formatPrice(-transaction.fee, {
                currency: transaction.currency,
                accounting: true
              }),
              description:
                (eventiveOrderId
                  ? transaction.description.indexOf(
                      "Application fee refund"
                    ) !== -1
                    ? `Eventive fee refund for order #${eventiveOrderId}`
                    : transaction.description.indexOf("REFUND FOR PAYMENT") ===
                      0
                    ? transaction.description.replace(
                        /(REFUND FOR PAYMENT|REFUND FOR CHARGE)/,
                        `REFUND FOR ORDER #${eventiveOrderId}`
                      )
                    : transaction.description.indexOf(
                        "Subscription creation"
                      ) !== -1
                    ? `Subscription: ${transaction.source.description}`
                    : null
                  : null) ||
                transaction.description ||
                transaction.id,
              available: {
                value: transaction.available_on,
                label: moment(transaction.available_on * 1000).format("MMM D")
              },
              actions: {
                value: 1,
                label: (
                  <span style={{ whiteSpace: "nowrap" }}>
                    {eventiveOrderId ? (
                      <Link to={`/orders/${eventiveOrderId}`} target="_blank">
                        View order
                      </Link>
                    ) : null}
                  </span>
                )
              }
            };
          })}
          columns={[
            { label: "Date", value: "date" },
            { label: "Type", value: "type" },
            { label: "Net", value: "net" },
            { label: "Amount", value: "amount" },
            { label: "Fee", value: "fee" },
            { label: "Description", value: "description" },
            { label: "Available", value: "available" },
            { label: "Actions", value: "actions" }
          ]}
        />
      </div>
    );
  }
}

export default connectToStores(Balance);



// WEBPACK FOOTER //
// ./src/app/components/balance.jsx